import {ElasticSearchResult} from '../storepage.def';

type StoreRecord = Record<string, unknown>;

interface StoreCardHit extends StoreRecord {
    _source: StoreRecord & {fsi: StoreRecord};
}

function isStoreRecord(value: unknown): value is StoreRecord {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isStoreCardHit(value: unknown): value is StoreCardHit {
    if (!isStoreRecord(value)) return false;
    const source = value._source;
    if (!isStoreRecord(source)) return false;
    return isStoreRecord(source.fsi);
}

/**
 * Accept a store search body only when the page can draw it.
 * Fast-shipping fields are stamped on a copy so a later hit cannot publish a half-updated list.
 * `fsi` has to be an object. Fields inside it, and the other card fields, may be absent.
 */
export function validateAndPrepareStoreResults(data: unknown, fastShipIds: readonly number[] | undefined): ElasticSearchResult | null {
    if (!isStoreRecord(data)) return null;

    const hitsEnvelope = data.hits;
    if (!isStoreRecord(hitsEnvelope) || !Array.isArray(hitsEnvelope.hits)) return null;

    const total = hitsEnvelope.total;
    if (!isStoreRecord(total) || typeof total.value !== 'number' || !Number.isFinite(total.value)) return null;

    const hits = hitsEnvelope.hits;
    const cardHits: StoreCardHit[] = [];
    for (let i = 0; i < hits.length; i++) {
        const hit = hits[i];
        if (!isStoreCardHit(hit)) return null;
        cardHits.push(hit);
    }

    const fastShipIdList = Array.isArray(fastShipIds) ? fastShipIds : [];
    const preparedHits = cardHits.map(hit => {
        const source = hit._source;
        const productId = source.ProductID;
        const isFastShip = typeof productId === 'number' && fastShipIdList.includes(productId);

        return {
            ...hit,
            _source: {
                ...source,
                fsi: {...source.fsi},
                is_fast_ship: isFastShip,
                sortFast: isFastShip ? -1 : source.sortBest,
            },
        };
    });

    return {
        ...data,
        hits: {
            ...hitsEnvelope,
            hits: preparedHits,
        },
    } as unknown as ElasticSearchResult;
}
